/**
 * vl-quiz-category-cards.js — 首页「Unsure which Ebike…」分类卡片轮播的交互
 *
 * 需求：需求/20260922-新版首页改版/子需求/05-首页UnsureWhichEbike分类卡片轮播/
 * 用户口径（2026-09-28）：
 *   - 卡片张数＝block 数（动态）；指示位＝每张卡一位（当前卡显示 48×8 进度条，其余为 8×8 圆点）
 *   - 自动播放语义**与 Hero 相同**：按该卡自己的时长（data-vl-duration 秒）推进，
 *     进度条＝当前卡的播放进度；手动切换/滚动后进度归零
 *   - **仅点击暂停按钮才暂停**（不做悬停暂停）；页面不可见时暂停（同 Hero）
 *   - reduced-motion 不自动播放；主题编辑器内不自动播放
 *
 * 交互对齐（2026-09-28 用户指定）：参照 apple.com/iphone-*-pro 的 `#highlights`（MediaCardGallery）——
 *   - 进入视口自动播放、离开视口自动暂停（非用户手动暂停时）；reduced-motion 不自动播放
 *   - 换卡为较长的缓动滑动（Apple 的 Slide duration 1s；此处 ~900ms ease-in-out）
 *
 * 用户 2026-09-28 追加修正：
 *   ①**不要重播按钮**：播完最后一张自动回到第 1 张重新开始（循环）
 *   ②滑动范围只到「最后一张进入内容区」（自然最大滚动量）；之后切换只推进 active（轨道不再左移）
 *
 * 用户 2026-10-12 追加：**PC 端 Hover「原地播放」**（口径经用户逐条确认）
 *   H1 仅 PC 且设备支持 hover（`(hover:hover) and (pointer:fine)`）；M/触屏不实现
 *   H2 hover 到「非 active」的**视频**卡 ⇒ ①active 卡**进入「暂停」态**（保留当前帧、**不显示封面**、底部进度条不动、按钮变 ▶）；②hover 卡**原地**播放（不动轨道、不改 active）
 *      —— 2026-10-12 用户修正：原「变回首帧 + 覆盖封面 + 进度清零」已废止；hover 结束后 active **从暂停处继续**（不再回头播）
 *   H3 hover 播放**循环**，**不推进** active、**不触发** goTo（轨道/圆点/进度条均不动）
 *   H4 鼠标移出**不立即停**；到**当前这一轮片尾**才判定：仍被 hover ⇒ 从 0 重播；否则**切回 active 并从 0 重新开始**
 *   H5 hover 到**另一张**卡 ⇒ 切换播放（原 hover 卡归零并回封面）
 *   H6 hover 播放中点击 ←/→ 或圆点 ⇒ **立即结束** hover 播放，按原逻辑滑到新 active 卡
 *   H7 不新增任何指示器；底部进度条/圆点仍代表 active
 *
 * 用户 2026-10-12（第二项）指示条交互对齐 apple.com/iphone-18-pro 的 timed dotnav：
 *   ①胶囊**定宽**（条目行 ＝ N×8 + 40 + (N−1)×12，与 active 位置无关）⇒ 不因快速连点/进度动画变长变短；
 *   ②切换时展开块**逐格交接**经过中间圆点（任一时刻相邻两位宽度互补 ⇒ 总和恒定），而非直接跳到目标。
 *   实测 Apple 逐帧宽度：48/8/8/8/8 → 45.1/10.9/8/8/8 → 31.2/24.8/8/8/8 → 8/45.9/10.1/8/8 → … → 8/8/8/8/48（总和恒 80）。
 *   另（用户 2026-10-12 拍板的默认）：hover 到 active 卡本身 ⇒ 退出 hover 播放；hover 到**图片卡** ⇒ 不触发并结束 hover；
 *   **暂停优先**（暂停中不触发、hover 中暂停即结束）；拖拽/滑动 ⇒ 同 H6；离开视口 ⇒ 结束 hover；
 *   `prefers-reduced-motion` 与主题编辑器内不生效（随 autoplay 门控）；后台开关 `enable_hover_play`（默认开）。
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || window.VLQuizCategoryCards) return;

  var BAR_W = 48; // 进度条（展开态）宽 px，与 CSS 的 48×8 一致
  var DOT_W = 8; // 圆点基础宽 px
  var PILL_STEP_MS = 200; // 指示条「逐步交接」每一步（相邻两位之间）的时长，对齐 Apple timed dotnav 实测 ~215ms/步
  var DRAG_MIN = 6; // 拖动判定阈值（px）
  var SCROLL_SETTLE_MS = 120; // 滚动结束后判定当前卡的延时
  var SCROLL_MS = 900; // 换卡滑动时长（对齐 Apple MediaCardGallery 的 Slide duration）
  var VIDEO_WAIT_MS = 3000; // 视频卡等不到元数据（时长）时的等待窗口；超时才回落到「展示时长」，避免轮播卡死

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  /* 悬停播放的设备/断点门控：≥768 且设备真支持 hover（触屏不参与）。每次调用实时判定，便于窗口缩放即时失效。 */
  function canHoverPlay() {
    try {
      return window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)').matches;
    } catch (e) {
      return false;
    }
  }

  function isDesignMode() {
    try {
      return !!(window.Shopify && window.Shopify.designMode);
    } catch (e) {
      return false;
    }
  }

  function supportsSmoothScroll() {
    try {
      return 'scrollBehavior' in document.documentElement.style;
    } catch (e) {
      return false;
    }
  }

  class VLQuizCategoryCards extends HTMLElement {
    connectedCallback() {
      try {
        if (this._initialised) return;
        this._initialised = true;

        this.track = this.querySelector('[data-vl-track]');
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-vl-card]'));
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-vl-item]'));
        this.count = this.cards.length;
        this.index = 0;
        this.progress = 0;
        this._elapsed = 0;
        this._videoWaitMs = 0;
        this._lastTs = 0;
        this._visible = !document.hidden;
        this._scrollLockUntil = 0; // 程序化滚动期间忽略 scroll 事件（避免把 active 判到别的卡）

        this.autoplay =
          this.dataset.vlAutoplay === 'true' && this.count > 1 && !isDesignMode() && !prefersReducedMotion();

        this.paused = false;
        this.userPaused = false;
        this._inView = true;
        // PC 端 Hover「原地播放」状态（2026-10-12）：hover 卡临时接管播放权
        this.hoverIndex = -1; // 当前因 hover 而播放的卡（-1 = 未处于 hover 播放）
        this._pointerCard = -1; // 指针当前所在的卡（用于片尾判定「是否仍被 hover」）
        this._hoverCleanup = []; // 事件解绑清单（规范 §3.6）
        this._pillPos = null; // 指示条「展开块」的浮点位置（逐步交接动效，2026-10-12）
        this._pillRaf = 0;
        this._tickBound = this._tick.bind(this);
        this._onScroll = this._handleScroll.bind(this);
        this._onVisibility = this._handleVisibility.bind(this);
        this._mq = null;
        this._onMq = this._handleViewportChange.bind(this);

        // 悬停播放总开关：后台开关 × autoplay 的各项前置门控
        // （reduced-motion / 主题编辑器 / 单卡 已在 this.autoplay 内排除；设备与断点门控在 canHoverPlay() 内实时判定）
        this.hoverPlay = this.dataset.vlHoverPlay !== 'false' && this.autoplay;

        this._bindControls();
        this._bindDrag();
        this._bindHoverPlay();
        this._setupInView();
        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        document.addEventListener('visibilitychange', this._onVisibility);
        try {
          this._mq = window.matchMedia('(max-width: 767px)');
          if (this._mq.addEventListener) this._mq.addEventListener('change', this._onMq);
          else if (this._mq.addListener) this._mq.addListener(this._onMq);
        } catch (e) {
          /* noop */
        }

        this._pillPos = this.index;
        this._writePill(this.index); // 先落一次宽度（避免首帧 8px 闪动）
        this._syncIndicator();
        this._syncPauseState();

        if (this.autoplay) this._start();
        else this._syncProgress(0);
      } catch (e) {
        /* noop */
      }
    }

    disconnectedCallback() {
      try {
        this._stop();
        this.track.removeEventListener('scroll', this._onScroll);
        document.removeEventListener('visibilitychange', this._onVisibility);
        try {
          if (this._mq) {
            if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._onMq);
            else if (this._mq.removeListener) this._mq.removeListener(this._onMq);
          }
        } catch (e) {
          /* noop */
        }
        if (this._io) this._io.disconnect();
        if (this._scrollRaf) window.cancelAnimationFrame(this._scrollRaf);
        if (this._pillRaf) window.cancelAnimationFrame(this._pillRaf);
        if (this._dragCleanup) this._dragCleanup();
        if (this._hoverCleanup) {
          this._hoverCleanup.forEach(function (fn) {
            try {
              fn();
            } catch (e) {
              /* noop */
            }
          });
          this._hoverCleanup = [];
        }
        if (this._scrollTimer) window.clearTimeout(this._scrollTimer);
      } catch (e) {
        /* noop */
      }
    }

    /* ------------------------------------------------------------- 控件绑定 */

    _bindControls() {
      var self = this;
      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-prev]'), function (btn) {
        btn.addEventListener('click', function () {
          self.goTo(self.index - 1);
        });
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-next]'), function (btn) {
        btn.addEventListener('click', function () {
          self.goTo(self.index + 1);
        });
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-goto]'), function (btn) {
        btn.addEventListener('click', function () {
          self.goTo(parseInt(btn.dataset.vlGoto, 10) || 0);
        });
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-pause]'), function (btn) {
        btn.addEventListener('click', function () {
          self.togglePause();
        });
      });
    }

    togglePause() {
      this.paused = !this.paused;
      this.userPaused = this.paused;
      if (this.paused) {
        this._clearHoverState(); // 暂停优先（用户 2026-10-12）：点击暂停立即结束 hover 播放
        this._stop();
      } else if (this.autoplay) {
        this._lastTs = 0;
        this._videoWaitMs = 0;
        this._start();
      }
      this._syncPauseState();
    }

    _syncPauseState() {
      var paused = this.paused === true;
      this.setAttribute('data-vl-paused', paused ? 'true' : 'false');
      if (paused || !this.autoplay) this._stop();
      this._syncVideos({});
      Array.prototype.forEach.call(this.querySelectorAll('[data-vl-pause]'), function (btn) {
        btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        btn.setAttribute('aria-label', paused ? 'Play' : 'Pause');
      });
    }

    /* 进入视口播放 / 离开视口暂停（Apple MediaCardGallery 的行为；用户手动暂停或已播完时不自动恢复） */
    _setupInView() {
      var self = this;
      try {
        if (typeof IntersectionObserver === 'undefined') return;
        this._io = new IntersectionObserver(
          function (entries) {
            var entry = entries[entries.length - 1];
            if (!entry) return;
            self._inView = entry.isIntersecting;
            if (!self.autoplay) return;
            if (self._inView) {
              if (!self.userPaused && !document.hidden) {
                self._lastTs = 0;
                self._videoWaitMs = 0;
                self._start();
              }
            } else {
              self._stop();
              self._endHoverPlay(); // 离开视口 ⇒ 结束 hover 播放（用户 2026-10-12 默认口径）
            }
            self._syncVideos({});
          },
          { threshold: [0.35] }
        );
        this._io.observe(this);
      } catch (e) {
        /* noop */
      }
    }

    /* ------------------------------------------------------------- 播放推进 */

    _start() {
      if (this._raf) return;
      this._lastTs = 0;
      this._raf = window.requestAnimationFrame(this._tickBound);
    }

    _stop() {
      if (this._raf) {
        window.cancelAnimationFrame(this._raf);
        this._raf = 0;
      }
    }

    _tick(ts) {
      this._raf = window.requestAnimationFrame(this._tickBound);
      if (!this._lastTs) {
        this._lastTs = ts;
        return;
      }
      var dt = ts - this._lastTs;
      this._lastTs = ts;
      if (!this.autoplay || this.paused || !this._visible || !this._inView || document.hidden) return;
      if (dt < 0 || dt > 2000) return;
      // PC hover「原地播放」期间：不推进 active、不切卡（用户 2026-10-12 H3）
      if (this.hoverIndex >= 0) return;

      var card = this.cards[this.index];
      var p = this._progressAt(card, dt);

      this._syncProgress(p);
      if (p >= 1) {
        this._elapsed = 0;
        // 播完最后一张 → 回到第 1 张重新开始（用户 2026-09-28：不要重播按钮、循环）
        this.goTo(this.index + 1);
      }
    }

    /* 当前卡的进度：**视频卡＝视频实际时长**（配置了视频就不再取「展示时长」）；图片卡＝「展示时长」 */
    _progressAt(card, dt) {
      if (!card || card.dataset.vlMedia !== 'video') {
        this._elapsed += dt;
        var dur = this._duration(this.index) * 1000;
        return dur > 0 ? Math.min(1, this._elapsed / dur) : 0;
      }

      var video = this._activeVideoOf(card);
      var hasDuration = video && isFinite(video.duration) && video.duration > 0;

      if (!hasDuration) {
        // 元数据还没到：进度条保持不动，并尝试自救播放；等满窗口仍拿不到时长 ⇒ 视为视频不可用，按「展示时长」兜底
        this._videoWaitMs += dt;
        this._tryPlay(video);
        if (this._videoWaitMs < VIDEO_WAIT_MS) return this.progress;
        this._elapsed += dt;
        var fallback = this._duration(this.index) * 1000;
        return fallback > 0 ? Math.min(1, this._elapsed / fallback) : 0;
      }

      this._videoWaitMs = 0;
      // 被浏览器阻止自动播放 / 被外部暂停 → 自救（用户主动暂停时不在此路径：_tick 已提前 return）
      if (video.paused && !video.ended) this._tryPlay(video);
      if (video.ended) return 1;
      return Math.max(0, Math.min(1, video.currentTime / video.duration));
    }

    _tryPlay(video) {
      if (!video || !video.paused || video.ended) return;
      try {
        var played = video.play();
        if (played && played.catch) played.catch(function () {});
      } catch (e) {
        /* noop */
      }
    }

    _duration(i) {
      var card = this.cards[i];
      if (!card) return 5;
      var d = parseFloat(card.dataset.vlDuration);
      if (!d || d <= 0) d = 5;
      if (d > 60) d = 60;
      return d;
    }

    /* ------------------------------------------------------------- 视频（2026-10-08 新增） */

    _videosIn(card) {
      if (!card) return [];
      return Array.prototype.slice.call(card.querySelectorAll('video'));
    }

    /* 当前视口下真正显示的那支视频（PC/M 各一支；只配一支时同一支服务两端） */
    _activeVideoOf(card) {
      var videos = this._videosIn(card);
      if (!videos.length) return null;
      var mobile = false;
      try {
        mobile = window.matchMedia('(max-width: 767px)').matches;
      } catch (e) {
        mobile = false;
      }
      for (var i = 0; i < videos.length; i++) {
        var isMobileVideo = videos[i].classList.contains('vl-qcc__video--m');
        if (mobile === isMobileVideo) return videos[i];
      }
      return videos[0];
    }

    _shouldPlayCard(i) {
      return !!(
        this.autoplay &&
        !this.paused &&
        this._visible &&
        this._inView &&
        !document.hidden &&
        i === this.index
      );
    }

    /* 只播当前卡：其余卡暂停并**回到首帧**（多卡同屏，避免同时播放） */
    _syncVideos(options) {
      var self = this;
      var reset = !!(options && options.reset);
      this.cards.forEach(function (card, i) {
        var videos = self._videosIn(card);
        if (!videos.length) return;
        var shown = self._activeVideoOf(card);
        var isCurrent = i === self.index; // 仍是「当前卡」吗？（用户 2026-10-08：切走后必须归零）
        var hover = self.hoverIndex;
        var isHoverCard = hover >= 0 && i === hover;
        // PC hover「原地播放」（2026-10-12）：hover 卡临时接管播放权；其余（含 active）一律不播
        var play =
          card.dataset.vlMedia === 'video' &&
          (isHoverCard ? self._hoverPlayingAllowed() : hover < 0 && self._shouldPlayCard(i));
        videos.forEach(function (video) {
          try {
            video.loop = false; // 必须以实际长度推进（loop 会让 ended 永不触发）
            if (video === shown && play) {
              if (reset) video.currentTime = 0;
              if (video.paused) {
                var played = video.play();
                if (played && played.catch) played.catch(function () {});
              }
            } else if (video === shown && isCurrent) {
              // **仍是当前卡**但没在播（用户暂停 / 离开视口 / 页面不可见 / **被 hover 播放覆盖**）：只暂停、**保留进度**
              // —— 用户 2026-10-12 修正：被 hover 覆盖的 active 卡**不再变回首帧**，而是「暂停」态（保留当前帧与进度）
              video.pause();
            } else {
              // **已被切换掉**的卡（或该断点未显示的备用视频）：暂停并**归零**
              // —— 用户 2026-10-08：切走后的视频进度一律重置为 0，回到该卡时从 0 开始（也避免进度条回跳闪变）
              // —— 用户 2026-10-12 修正后：hover 播放期间**只有非 hover、非 active 的卡**走这里（active 走上一支「只暂停」）
              //    刚结束的 hover 卡、以及被切走的卡仍在此归零 ⇒ 回到封面 / 首帧
              video.pause();
              video.currentTime = 0;
            }
          } catch (e) {
            /* noop */
          }
        });
      });
    }

    /* ------------------------------------------------------------- PC 端 Hover「原地播放」（2026-10-12） */

    /* 悬停播放是否被允许：总开关 × 设备/断点（实时） × 与 autoplay 相同的运行门控 */
    _hoverPlayingAllowed() {
      return !!(
        this.hoverPlay &&
        canHoverPlay() &&
        !this.paused &&
        this._visible &&
        this._inView &&
        !document.hidden
      );
    }

    /* 绑定：每张卡的 pointerenter/pointerleave ＋ 每支视频的 ended（片尾判定用）。非 PC / 不支持 hover 时完全不绑定。 */
    _bindHoverPlay() {
      if (!this.hoverPlay) return;
      var self = this;
      this.cards.forEach(function (card, i) {
        var onEnter = function () {
          self._cardPointerEnter(i);
        };
        var onLeave = function () {
          // 移出**不停止**（H4）：只记状态，是否结束留到「片尾判定」
          if (self._pointerCard === i) self._pointerCard = -1;
        };
        card.addEventListener('pointerenter', onEnter);
        card.addEventListener('pointerleave', onLeave);
        self._hoverCleanup.push(function () {
          card.removeEventListener('pointerenter', onEnter);
          card.removeEventListener('pointerleave', onLeave);
        });
        self._videosIn(card).forEach(function (video) {
          var onEnded = function (ev) {
            self._onVideoEnded(ev, i);
          };
          video.addEventListener('ended', onEnded);
          self._hoverCleanup.push(function () {
            video.removeEventListener('ended', onEnded);
          });
        });
      });
    }

    /* 指针进入某张卡：H2 / H5 ＋ 用户 2026-10-12 拍板的默认口径 */
    _cardPointerEnter(i) {
      if (!this.hoverPlay) return;
      this._pointerCard = i;
      if (this.paused) return; // 暂停优先：暂停中不触发悬停播放
      if (i === this.index) {
        // hover 到 active（中心）卡本身 ⇒ 退出 hover 播放、恢复正常
        this._endHoverPlay();
        return;
      }
      var card = this.cards[i];
      if (!card || card.dataset.vlMedia !== 'video') {
        // hover 到未配视频的图片卡 ⇒ 不触发，并结束当前 hover 播放
        this._endHoverPlay();
        return;
      }
      if (!this._hoverPlayingAllowed()) return; // 不在视口 / 页面不可见等
      this._startHoverPlay(i);
    }

    /* 开始（或切换）hover 播放：hover 卡接管播放权；active 停播 + 封面 + 进度清零 */
    _startHoverPlay(i) {
      if (this.hoverIndex === i) return; // 同一张卡：延续播放（不重头）
      var self = this;
      this.hoverIndex = i;
      this.setAttribute('data-vl-hovering', 'true');
      this.cards.forEach(function (card, k) {
        if (k === i) card.setAttribute('data-vl-hover', 'true');
        else card.removeAttribute('data-vl-hover');
      });
      // 被覆盖的 active 卡 ⇒ 进入「暂停」态（用户 2026-10-12 修正）：
      //   · 保留当前帧（**不回头帧**）、**不显示封面**、底部**进度条保持不动**、按钮变 ▶（见 CSS 的 data-vl-hover-paused）
      var activeCard = this.cards[this.index];
      if (activeCard) activeCard.setAttribute('data-vl-hover-paused', 'true');
      this._videoWaitMs = 0;
      // 不重置 _elapsed / progress（进度条保持不动）；自动推进由 _tick 直接 return 停住
      this._syncVideos({ reset: true }); // reset 只作用于「在播的 hover 卡」（从 0 起播）；active 走「只暂停、保留进度」
      this._lastTs = 0;
    }

    /* 只清 hover 状态、不动播放：用于「马上要换 active」的场景（goTo / 拖动 / 暂停） */
    _clearHoverState() {
      if (this.hoverIndex < 0) return;
      this.hoverIndex = -1;
      this.removeAttribute('data-vl-hovering');
      this.cards.forEach(function (card) {
        card.removeAttribute('data-vl-hover');
        card.removeAttribute('data-vl-hover-paused');
      });
    }

    /* 结束 hover 播放 ⇒ 被覆盖的 active 卡从**暂停处继续**（用户 2026-10-12 修正：不再回头播） */
    _endHoverPlay() {
      if (this.hoverIndex < 0) return;
      this._clearHoverState();
      this._videoWaitMs = 0;
      // 不重置 _elapsed / progress：进度条从冻结位置继续；active 视频从暂停处恢复
      this._syncVideos({}); // 不带 reset：active 恢复播放（从暂停点）；刚结束的 hover 卡回封面并归零
      this._lastTs = 0;
    }

    /* hover 卡视频的片尾判定（H4）：仍被悬停 ⇒ 从 0 重播；否则切回 active */
    _onVideoEnded(ev, i) {
      if (i !== this.hoverIndex) return; // 非 hover 卡：active 的 ended 仍由 _tick 的推进逻辑处理
      var video = ev && ev.target;
      if (!video) return;
      if (this._pointerCard === i && this._hoverPlayingAllowed()) {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* noop */
        }
        this._tryPlay(video);
      } else {
        this._endHoverPlay();
      }
    }

    /* ------------------------------------------------------------- 指示条与切换 */

    _syncProgress(p) {
      this.progress = p;
      var item = this.items[this.index];
      if (!item) return;
      var fill = item.querySelector('[data-vl-bar-fill]');
      if (fill) fill.style.width = (BAR_W * p).toFixed(2) + 'px';
    }

    _syncIndicator() {
      var self = this;
      this.items.forEach(function (item, i) {
        if (i === self.index) item.setAttribute('data-vl-active', 'true');
        else item.removeAttribute('data-vl-active');
        var fill = item.querySelector('[data-vl-bar-fill]');
        if (fill) fill.style.width = i === self.index ? fill.style.width || '0px' : '0px';
      });
      this.cards.forEach(function (card, i) {
        if (i === self.index) card.setAttribute('data-vl-active', 'true');
        else card.removeAttribute('data-vl-active');
      });
      this._syncProgress(this.progress);
      this._syncVideos({ reset: true });
    }

    /* ------------------------------------------------------------- 指示条「逐步交接」动效（2026-10-12）
       Apple timed dotnav（#iphone-18-pro 实测）的机制：容器定宽；切换时展开块**一格一格交接**过去
       （途经中间的圆点），任一时刻只有相邻两位处于部分展开/收缩且**宽度互补**（45.1+10.9 = 31.2+24.8 = 56）
       ⇒ 各点宽度之和恒定、胶囊（bot 条）不会变长变短。
       实现：把「展开块位置」当成浮点数 pos 逐帧推进，每点宽度 ＝ 48 − 40 × min(1, |i − pos|)。
       语义（哪个卡是 active、fill 在哪）仍由 `this.index` 立即切换 —— 与 Apple 一致（`.current` 第一帧即跳）。 */
    _writePill(pos) {
      this.items.forEach(function (item, i) {
        var dot = item.querySelector('.vl-qcc__dot');
        if (!dot) return;
        var t = Math.min(1, Math.abs(i - pos));
        dot.style.width = (BAR_W - (BAR_W - DOT_W) * t).toFixed(2) + 'px';
      });
    }

    _animatePill(target, options) {
      var self = this;
      if (this._pillRaf) {
        window.cancelAnimationFrame(this._pillRaf);
        this._pillRaf = 0;
      }
      if (this.count < 1) return;
      var from = typeof this._pillPos === 'number' ? this._pillPos : target;
      var total = Math.abs(target - from);
      var instant = (options && options.instant) || prefersReducedMotion();
      if (instant || typeof window.requestAnimationFrame !== 'function' || total < 0.001) {
        this._pillPos = target;
        this._writePill(target);
        return;
      }
      var dir = target > from ? 1 : -1;
      var dur = total * PILL_STEP_MS;
      var t0 = 0;
      var step = function (ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / dur);
        if (p >= 1) {
          self._pillPos = target;
          self._writePill(target);
          self._pillRaf = 0;
          return;
        }
        // 每一步各自 ease-in-out（Apple 观感：每格先加速后收住）；同时保证任一时刻两位宽度互补
        var x = total * p;
        var idx = Math.floor(x);
        var frac = x - idx;
        var pos = from + dir * (idx + frac * frac * (3 - 2 * frac));
        self._pillPos = pos;
        self._writePill(pos);
        self._pillRaf = window.requestAnimationFrame(step);
      };
      this._pillPos = from;
      this._pillRaf = window.requestAnimationFrame(step);
    }

    _padLeft() {
      try {
        var pad = parseFloat(getComputedStyle(this.track).paddingLeft);
        return isNaN(pad) ? 0 : pad;
      } catch (e) {
        return 0;
      }
    }

    _cardRestLeft(card) {
      var trackRect = this.track.getBoundingClientRect();
      return card.getBoundingClientRect().left - trackRect.left + this.track.scrollLeft;
    }

    goTo(i, options) {
      if (this.count < 1) return;
      // PC hover 播放中点击 ←/→ 或圆点：立即结束 hover 播放、按原逻辑走（用户 2026-10-12 H6）
      this._clearHoverState();
      i = ((i % this.count) + this.count) % this.count;
      // 目标位置（用户 2026-10-08）：≥768 让当前卡**居中**（两侧到头时贴边 ⇒ 该卡只在边缘播放、轨道不再移动）；
      // <768 沿用设计稿的左侧对齐。范围仍夹在自然滚动量之内（2026-09-28 修正 ② 保持有效）。
      var left = this._targetScrollLeft(i);
      var instant = (options && options.instant) || prefersReducedMotion();
      // 本组件的 active 由自动播放/圆点/箭头显式驱动；程序化滚动期间（含滚动到底后仅推进 active）
      // 一律不参与「按滚动位置反推 active」，否则多张卡目标位置被夹到同一 max 时会误判（2026-09-28 修复）
      this._scrollLockUntil = (window.performance && performance.now ? performance.now() : Date.now()) + SCROLL_MS + 300;
      this._scrollTo(left, instant);
      this.index = i;
      this._elapsed = 0;
      this._videoWaitMs = 0;
      this.progress = 0;
      this._syncIndicator();
      this._animatePill(i); // 指示条逐步交接过去（途经中间圆点），而非直接跳到目标
    }

    /* 目标滚动位置：≥768 让当前卡居中（可居中时）；两侧到头则贴边（该卡就在边缘播放，轨道不再移动） */
    _targetScrollLeft(i) {
      var card = this.cards[i];
      if (!card) return 0;
      var max = this._maxScroll();
      var left;
      if (this._isPc()) {
        var w = card.getBoundingClientRect().width;
        left = this._cardRestLeft(card) + w / 2 - this.track.clientWidth / 2;
      } else {
        left = this._cardRestLeft(card) - this._padLeft();
      }
      return Math.min(Math.max(0, left), max);
    }

    _isPc() {
      try {
        return window.matchMedia('(min-width: 768px)').matches;
      } catch (e) {
        return true;
      }
    }

    _maxScroll() {
      var max = this.track.scrollWidth - this.track.clientWidth;
      return max > 0 ? max : 0;
    }

    /* 换卡滑动：自写缓动（~900ms ease-in-out，对齐 Apple 的 Slide duration），期间关闭 scroll-snap */
    _scrollTo(left, instant) {
      var self = this;
      if (this._scrollRaf) {
        window.cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = 0;
      }
      if (instant || typeof window.requestAnimationFrame !== 'function') {
        this.track.scrollLeft = left;
        return;
      }
      var start = this.track.scrollLeft;
      var distance = left - start;
      if (Math.abs(distance) < 1) return;
      this.track.style.scrollSnapType = 'none';
      var t0 = 0;
      var step = function (ts) {
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / SCROLL_MS);
        var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        self.track.scrollLeft = start + distance * eased;
        if (p < 1) {
          self._scrollRaf = window.requestAnimationFrame(step);
        } else {
          self._scrollRaf = 0;
          self.track.style.scrollSnapType = '';
        }
      };
      this._scrollRaf = window.requestAnimationFrame(step);
    }

    _nearestIndex() {
      var scroll = this.track.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < this.count; i++) {
        var target = this._targetScrollLeft(i);
        var d = Math.abs(target - scroll);
        // 并列时取**更靠前**的卡（滚动到底时最后几张目标位置相同 → 取仍对齐在起点的那张）
        if (d < bestDist || (d === bestDist && i < best)) {
          bestDist = d;
          best = i;
        }
      }
      return best;
    }

    _handleScroll() {
      var self = this;
      if (this._isDragging) return;
      var now = window.performance && performance.now ? performance.now() : Date.now();
      if (now < this._scrollLockUntil) return;
      if (this._scrollTimer) window.clearTimeout(this._scrollTimer);
      this._scrollTimer = window.setTimeout(function () {
        // 拖动/滑动轨道 ⇒ 结束 hover 播放（用户 2026-10-12 默认口径：与点击切换同口径）
        self._clearHoverState();
        var i = self._nearestIndex();
        if (i !== self.index) {
          self.index = i;
          self._elapsed = 0;
          self._videoWaitMs = 0;
          self.progress = 0;
          self._syncIndicator();
          self._animatePill(i);
        }
      }, SCROLL_SETTLE_MS);
    }

    /* ------------------------------------------------------------- 拖拽 */

    _bindDrag() {
      var self = this;
      var startX = 0;
      var startScroll = 0;
      var moved = false;
      var pointerId = null;

      function onDown(event) {
        if (event.pointerType === 'touch') return; // 触摸交给原生滚动
        if (event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = self.track.scrollLeft;
        moved = false;
        self._isDragging = false;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
      }

      function onMove(event) {
        if (pointerId === null || event.pointerId !== pointerId) return;
        var dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > DRAG_MIN) {
          moved = true;
          self._isDragging = true;
          self.track.style.scrollSnapType = 'none';
          try {
            self.track.setPointerCapture(pointerId);
          } catch (e) {
            /* noop */
          }
        }
        if (!moved) return;
        self.track.scrollLeft = startScroll - dx;
        event.preventDefault();
      }

      function onUp(event) {
        if (pointerId === null || (event && event.pointerId !== undefined && event.pointerId !== pointerId)) {
          return;
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (moved) {
          self.track.style.scrollSnapType = '';
          self.goTo(self._nearestIndex());
          // 拖动结束后抑制随后的 click，避免误触卡片链接
          var suppress = function (e) {
            e.preventDefault();
            e.stopPropagation();
          };
          window.addEventListener('click', suppress, { capture: true, once: true });
          window.setTimeout(function () {
            window.removeEventListener('click', suppress, { capture: true });
          }, 300);
        }
        pointerId = null;
        setTimeout(function () {
          self._isDragging = false;
        }, 0);
      }

      this.track.addEventListener('pointerdown', onDown);
      this._dragCleanup = function () {
        self.track.removeEventListener('pointerdown', onDown);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
    }

    /* ------------------------------------------------------------- 页面可见性 */

    _handleVisibility() {
      this._visible = !document.hidden;
      if (this.autoplay && !this.paused && this._visible) {
        this._lastTs = 0;
        this._videoWaitMs = 0;
        this._start();
      }
      this._syncVideos({});
    }

    /* PC/M 断点变化：换持「当前视口显示的那支视频」继续播放 */
    _handleViewportChange() {
      // 跨到 M（不支持悬停）⇒ 结束 hover 状态（用户 2026-10-12）
      if (!canHoverPlay()) this._endHoverPlay();
      this._syncVideos({});
    }
  }

  if (!customElements.get('vl-quiz-category-cards')) {
    customElements.define('vl-quiz-category-cards', VLQuizCategoryCards);
  }

  window.VLQuizCategoryCards = { element: VLQuizCategoryCards, barWidth: BAR_W };
})();
